import { getMetaTagContentByName } from '@/utils/head-meta';
import camelcaseKeys from 'camelcase-keys';
import decamelizeKeys from 'decamelize-keys';
import { ApiClient } from '@/lib/api-client-base';

const ALLOWED_CUSTOM_HEADERS = [
  'Content-Type',
  'Accept',
  'Authorization',
  'Cache-Control',
  'User-Agent',
  'If-Match',
  'If-None-Match',
  'If-Modified-Since',
  'If-Unmodified-Since',
] as const;

function buildSecureHeaders(customHeaders?: HeadersInit): Record<string, string> {
  // Filter custom headers to only allow safe ones
  const allowedCustomHeaders = customHeaders
    ? Object.fromEntries(
        Object.entries(customHeaders).filter(([key]) =>
          ALLOWED_CUSTOM_HEADERS.includes(key as (typeof ALLOWED_CUSTOM_HEADERS)[number]),
        ),
      )
    : {};

  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...allowedCustomHeaders,
    'X-CSRF-Token': getMetaTagContentByName('csrf-token', ''), // Applied last - cannot be overridden
  };
}

export class FetchApiError extends Error {
  ok = false as const;
  status: number;
  statusText: string;
  headers: Headers;
  url: string;
  body: unknown;

  constructor(response: Response, body: unknown) {
    const errorMessage =
      typeof body === 'object' && body !== null
        ? ((body as { message?: string }).message ??
          (body as { error?: string }).error ??
          (body as { errors?: { message: string }[] }).errors?.[0]?.message)
        : undefined;

    super(errorMessage ?? response.statusText);
    this.name = 'FetchApiError';
    this.status = response.status;
    this.statusText = response.statusText;
    this.headers = response.headers;
    this.url = response.url;
    this.body = body;
  }
}

interface FetchApiMeta {
  ok: true;
  status: number;
  statusText: string;
  headers: Headers;
  url: string;
}

export interface FetchApiMetaResult<T> {
  data: T;
  meta: FetchApiMeta;
}

interface IntercomApiClientContext {
  appId: string;
}

type JsonParsed = Record<string, unknown> | readonly Record<string, unknown>[];

type IntercomApiClientError = FetchApiError;

/**
 * API client implementation for the Intercom API. Provides a consistent interface for
 * making requests to the Intercom API.
 *
 * @note The client applies a number of transformations to requests and responses to
 * improve developer experience and ensure consistent API usage.
 *
 * - **snake_case request data**: The client converts request data to snake_case which
 *   is the expected format for the Intercom API. **Note**: This is not applied to requests
 *   which have a body of type `FormData`.
 *
 * - **camelCase response data**: The client converts response data to camelCase which
 *   is format expected by the frontend.
 *
 * - **app_id query parameter**: The client automatically includes the app_id as a
 * query parameter for all requests.
 *
 * - **app_id in request body**: The client automatically includes the app_id in the
 *   request body for POST/PUT/PATCH requests.
 *
 * - **error transformation**: The client transforms error responses into FetchApiError
 *   instances which provide access to the error body and status code.
 *
 * @example Static URL request
 * ```ts
 * const users = await apiClient.get('/api/v1/users');
 * ```
 *
 * @example Usage with dynamic URL
 * ```ts
 * const user = await apiClient.post(({ appId }) => `/api/v1/apps/${appId}/users`, { name: 'John Doe' });
 * ```
 *
 * @example Error handling
 * ```ts
 * try {
 *   await apiClient.post('/api/v1/users', { name: 'John' });
 * } catch (error) {
 *   console.log('Error status:', error.status);
 *   console.log('Error body:', error.body);
 * }
 * ```
 *
 * @see {@link ApiClient} for more usage examples.
 */
export const apiClient = new ApiClient<IntercomApiClientContext, IntercomApiClientError>({
  context: {
    appId: getMetaTagContentByName('app_id_code'),
  },
  buildHeaders: buildSecureHeaders,
  transformUrl: (url, context) => {
    const urlObj = new URL(url, window.location.origin);
    if (context.appId) {
      urlObj.searchParams.set('app_id', context.appId);
    }
    return urlObj.toString();
  },
  transformResponse: async (response) => {
    const text = await response.text();
    if (!text) {
      return null;
    }
    const parsedData = JSON.parse(text) as JsonParsed;
    return camelcaseKeys(parsedData, { deep: true });
  },
  transformError: async (response) => {
    const text = await response.text();
    let errorBody: unknown;
    try {
      if (text) {
        const parsedError = JSON.parse(text) as JsonParsed;
        errorBody = camelcaseKeys(parsedError, { deep: true });
      } else {
        errorBody = null;
      }
    } catch {
      errorBody = text;
    }
    return new FetchApiError(response, errorBody);
  },
  transformRequestBody: (body, context, method) => {
    const decamelizedBody = decamelizeKeys(body, { deep: true });
    // The Intercom API expects app_id to be in the request body for POST and PUT requests.
    // This eliminates the need to manually include it in the request body.
    if ((method === 'POST' || method === 'PUT') && context.appId) {
      decamelizedBody.app_id = context.appId;
    }
    return decamelizedBody;
  },
  transformRequestFormData: (formData, context, method) => {
    if ((method === 'POST' || method === 'PUT') && context.appId) {
      formData.append('app_id', context.appId);
    }
    return formData;
  },
});
